import type { GetRoles200Item } from 'generatedTypes/user/models';
import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useToast from 'hooks/useToast';

import useTranslation from './hooks/useTranslation';

import { ALL_ROLES_API, USER_ROLES_BASE_API } from './constants';

const useUserRoles = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { broadcastToast } = useToast();
    const { FETCHING_USER_ROLES_ERROR_MESSAGE, UPDATING_USER_ROLES_SUCCESS_MESSAGE } =
        useTranslation();
    const { authenticatedDelete, authenticatedGet, authenticatedPut } = useFetch();

    const fetchAllRoles = useCallback(async (): Promise<GetRoles200Item[]> => {
        setIsLoading(true);
        const url = ALL_ROLES_API;

        try {
            const { data } = await authenticatedGet<GetRoles200Item[]>(url);

            return data ?? [];
        } catch (error) {
            broadcastToast({
                text: FETCHING_USER_ROLES_ERROR_MESSAGE,
                type: 'error'
            });
            throw new Error(JSON.stringify(error));
        } finally {
            setIsLoading(false);
        }
    }, [FETCHING_USER_ROLES_ERROR_MESSAGE, authenticatedGet, broadcastToast]);

    const fetchRoles = useCallback(
        async (id: string): Promise<string[]> => {
            setIsLoading(true);
            const url = `${USER_ROLES_BASE_API}/${id}/roles`;

            try {
                const { data } = await authenticatedGet<string[]>(url);

                return data ?? [];
            } catch (error) {
                broadcastToast({
                    text: FETCHING_USER_ROLES_ERROR_MESSAGE,
                    type: 'error'
                });
                throw new Error(JSON.stringify(error));
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedGet, broadcastToast, FETCHING_USER_ROLES_ERROR_MESSAGE]
    );

    const fetchUserRoles = useCallback(
        async (userId: string): Promise<string[]> => {
            const roles = await fetchRoles(userId);

            return roles;
        },
        [fetchRoles]
    );

    const fetchMyRoles = useCallback(async (): Promise<string[]> => {
        const roles = await fetchRoles('me');

        return roles;
    }, [fetchRoles]);

    const updateUserRoles = useCallback(
        async (userId: string, role: string): Promise<void> => {
            setIsLoading(true);
            const url = `${USER_ROLES_BASE_API}/${userId}/roles/${role}`;
            const currentRoles = await fetchUserRoles(userId);

            try {
                await authenticatedPut<string[]>(url, undefined);

                const rolesToRemove = currentRoles.filter(userRole => userRole !== role);

                if (rolesToRemove.length > 0) {
                    await Promise.allSettled(
                        rolesToRemove.map(role =>
                            authenticatedDelete(`${USER_ROLES_BASE_API}/${userId}/roles/${role}`)
                        )
                    );
                }

                broadcastToast({
                    text: UPDATING_USER_ROLES_SUCCESS_MESSAGE,
                    type: 'success'
                });
            } catch (error) {
                broadcastToast({
                    text: FETCHING_USER_ROLES_ERROR_MESSAGE,
                    type: 'error'
                });
                throw new Error(JSON.stringify(error));
            } finally {
                setIsLoading(false);
            }
        },
        [
            authenticatedDelete,
            authenticatedPut,
            broadcastToast,
            FETCHING_USER_ROLES_ERROR_MESSAGE,
            fetchUserRoles,
            UPDATING_USER_ROLES_SUCCESS_MESSAGE
        ]
    );

    return {
        fetchAllRoles,
        fetchMyRoles,
        fetchUserRoles,
        isLoading,
        updateUserRoles
    };
};

export default useUserRoles;
